/**
 * `Newswire` — verified-source live news rail.
 *
 * Fetches the latest 25 verified news items from `news.verifiedList` via
 * TanStack Query, then streams live prepends from `/sse/news/verified`.
 * Items are deduplicated by `id` and capped at 50 in memory.
 *
 * Accessibility:
 *   - `aria-live="polite"` region announces new items at most once per 5 s
 *     to comply with WCAG SC 4.1.3.
 *   - Each item row is ≥ 44 px tall (WCAG SC 2.5.5).
 *   - Directional icons mirror via `rtl:scale-x-[-1]`.
 *
 * RTL: logical CSS properties only (ms-*, me-*, text-start, etc.).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";

import type { VerifiedNewsFrame } from "#/hooks/useNewswireStream.js";
import { useSSE } from "#/hooks/useSSE.js";
import type { TranslateFn } from "#/lib/i18n.js";

/* ---------------------------------------------------------------
 * Constants
 * --------------------------------------------------------------- */

const MAX_ITEMS = 50;
const LIVE_ANNOUNCE_INTERVAL_MS = 5_000;
const INITIAL_LIMIT = 25;

/* ---------------------------------------------------------------
 * API fetch helper
 * --------------------------------------------------------------- */

async function fetchVerifiedNews(
  apiBaseUrl: string,
  limit: number,
): Promise<VerifiedNewsFrame[]> {
  const base = apiBaseUrl.replace(/\/+$/u, "");
  // tRPC GET: input encoded as SuperJSON-wrapped object.
  const input = encodeURIComponent(JSON.stringify({ json: { limit } }));
  const url = `${base}/api/trpc/news.verifiedList?input=${input}`;

  const res = await fetch(url, {
    method: "GET",
    headers: { accept: "application/json" },
    credentials: "omit",
  });
  if (!res.ok) throw new Error(`news.verifiedList ${res.status}`);

  const json = (await res.json()) as {
    result?: { data?: { json?: VerifiedNewsFrame[] } | VerifiedNewsFrame[] };
  };
  const inner = json.result?.data;
  const rows =
    inner && typeof inner === "object" && "json" in inner
      ? (inner as { json: VerifiedNewsFrame[] }).json
      : (inner as VerifiedNewsFrame[] | undefined);

  if (!rows) throw new Error("news.verifiedList: malformed response");
  return rows;
}

/* ---------------------------------------------------------------
 * Helpers
 * --------------------------------------------------------------- */

const SEVERITY_COLOR: Record<string, string> = {
  urgent: "var(--color-accent-emergency)",
  caution: "var(--color-accent-warning)",
  high:   "var(--color-accent-emergency)",
  medium: "var(--color-accent-warning)",
};

function getSeverityColor(sev: string): string {
  return SEVERITY_COLOR[sev] ?? "var(--color-accent-info)";
}

function relativeTime(iso: string, locale: string): string {
  const d = new Date(iso);
  const diffSecs = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diffSecs < 5) {
    return locale === "ar" ? "الآن" : locale === "fr" ? "à l'instant" : "just now";
  }
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  if (diffSecs < 60) return rtf.format(-diffSecs, "second");
  if (diffSecs < 3_600) return rtf.format(-Math.floor(diffSecs / 60), "minute");
  if (diffSecs < 86_400) return rtf.format(-Math.floor(diffSecs / 3_600), "hour");
  return rtf.format(-Math.floor(diffSecs / 86_400), "day");
}

/* ---------------------------------------------------------------
 * Props
 * --------------------------------------------------------------- */

export interface NewswireProps {
  locale: string;
  t: TranslateFn;
  apiBaseUrl?: string;
}

/* ---------------------------------------------------------------
 * Skeleton (shown during initial fetch)
 * --------------------------------------------------------------- */

function NewswireSkeleton({ t }: { t: TranslateFn }) {
  return (
    <div className="rounded-lg border border-border bg-card" aria-busy="true">
      <div className="px-4 py-3 border-b border-border">
        <div className="h-5 w-44 bg-muted rounded animate-pulse" aria-hidden="true" />
      </div>
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="px-4 py-3 border-b border-border last:border-0 space-y-2">
          <div className="flex items-center gap-2">
            <div className="h-3 w-14 bg-muted rounded animate-pulse" aria-hidden="true" />
            <div className="h-3 w-10 bg-muted rounded animate-pulse ms-auto" aria-hidden="true" />
          </div>
          <div className="h-4 w-full bg-muted rounded animate-pulse" aria-hidden="true" />
          <div className="h-4 w-4/5 bg-muted rounded animate-pulse" aria-hidden="true" />
        </div>
      ))}
      <p className="sr-only">{t("newswire.loading")}</p>
    </div>
  );
}

/* ---------------------------------------------------------------
 * Inner component (receives seeded initialItems)
 * --------------------------------------------------------------- */

interface NewswireInnerProps {
  initialItems: VerifiedNewsFrame[];
  locale: string;
  t: TranslateFn;
  sseBaseUrl: string;
}

function NewswireInner({ initialItems, locale, t, sseBaseUrl }: NewswireInnerProps) {
  const [items, setItems] = useState<VerifiedNewsFrame[]>(initialItems);
  const [liveItem, setLiveItem] = useState<VerifiedNewsFrame | null>(null);
  const lastAnnounceRef = useRef<number>(0);

  const handleVerifiedNews = useCallback((data: VerifiedNewsFrame) => {
    setItems((prev) => {
      if (prev.some((it) => it.id === data.id)) return prev;
      const next = [data, ...prev];
      return next.length > MAX_ITEMS ? next.slice(0, MAX_ITEMS) : next;
    });
    const now = Date.now();
    if (now - lastAnnounceRef.current >= LIVE_ANNOUNCE_INTERVAL_MS) {
      lastAnnounceRef.current = now;
      setLiveItem(data);
    }
  }, []);

  const { status } = useSSE<"news.verified", VerifiedNewsFrame>(
    `${sseBaseUrl}/sse/news/verified`,
    { "news.verified": handleVerifiedNews },
  );

  const isReconnecting = status === "reconnecting";

  return (
    <div className="rounded-lg border border-border bg-card flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
        <h2 className="text-sm font-semibold text-foreground">{t("newswire.title")}</h2>
        {isReconnecting && (
          <span className="text-xs text-muted-foreground">{t("newswire.reconnecting")}</span>
        )}
      </div>

      {/* aria-live region — announces new items, throttled to 5 s */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {liveItem ? `${liveItem.headline} — ${liveItem.source}` : ""}
      </div>

      {/* Items list */}
      <div className="flex-1 overflow-y-auto divide-y divide-border">
        {items.length === 0 ? (
          <p className="px-4 py-6 text-sm text-muted-foreground text-center">
            {t("newswire.empty")}
          </p>
        ) : (
          items.map((item) => (
            <NewsItem key={item.id} item={item} locale={locale} t={t} />
          ))
        )}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------
 * Single news item row
 * --------------------------------------------------------------- */

interface NewsItemProps {
  item: VerifiedNewsFrame;
  locale: string;
  t: TranslateFn;
}

function NewsItem({ item, locale, t }: NewsItemProps) {
  const dotColor = getSeverityColor(item.severity);

  return (
    <article className="relative group" data-testid="newswire-entry">
      <a
        href={item.sourceUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={[
          "flex flex-col gap-1 px-4 py-3 min-h-[44px]",
          "hover:bg-accent/50 transition-colors duration-150",
          "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-primary",
        ].join(" ")}
        aria-label={`${item.headline} — ${item.source}`}
      >
        {/* Source badge + severity dot + timestamp */}
        <div className="flex items-center gap-2">
          <span
            aria-hidden="true"
            className="h-2 w-2 rounded-full shrink-0"
            style={{ backgroundColor: dotColor }}
          />
          <span className="text-xs font-medium text-muted-foreground truncate max-w-[120px]">
            {item.source}
          </span>
          <span className="text-xs text-muted-foreground ms-auto shrink-0">
            {relativeTime(item.publishedAt, locale)}
          </span>
          <ExternalLink
            className="h-3 w-3 shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity duration-150 rtl:scale-x-[-1]"
            aria-hidden="true"
          />
        </div>

        {/* Headline */}
        <p className="text-sm text-foreground line-clamp-2 leading-snug">
          {item.headline}
        </p>

        {/* Summary (when available) */}
        {item.summary && (
          <p className="text-xs text-muted-foreground line-clamp-1">{item.summary}</p>
        )}

        {/* Verified badge */}
        <span className="sr-only">
          {`${t("newswire.source")}: ${item.source}`}
        </span>
      </a>
    </article>
  );
}

/* ---------------------------------------------------------------
 * Newswire (public export) — handles initial fetch then delegates
 * --------------------------------------------------------------- */

export function Newswire({ locale, t, apiBaseUrl }: NewswireProps) {
  const base =
    apiBaseUrl ??
    (typeof import.meta !== "undefined"
      ? (import.meta.env?.VITE_API_URL as string | undefined)
      : undefined) ??
    "";

  const { data: initialItems, isLoading, isError } = useQuery<VerifiedNewsFrame[]>({
    queryKey: ["news", "verifiedList", INITIAL_LIMIT],
    queryFn: () => fetchVerifiedNews(base, INITIAL_LIMIT),
    staleTime: 60_000,
    retry: 2,
  });

  if (isLoading) return <NewswireSkeleton t={t} />;

  if (isError) {
    return (
      <div className="rounded-lg border border-border bg-card px-4 py-8 text-center">
        <p className="text-sm text-muted-foreground">{t("newswire.empty")}</p>
      </div>
    );
  }

  return (
    <NewswireInner
      initialItems={initialItems ?? []}
      locale={locale}
      t={t}
      sseBaseUrl={base.replace(/\/+$/u, "")}
    />
  );
}

export default Newswire;
